import * as React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import OrganizationContextSwitcher from "../organizations/ContextSwitcher";
import { GetContext, UnsubscribeContext } from "../general/ApplicationContext";
import { GetCurrentLoggedInUser, ROLE_CLAIM } from "../account/AccountStore";
import { ListManagedServices, ManagedServiceType, AzureManagedService } from "../managedServices/ManagedServiceStore";
import axios from "../general/HttpClient";
import { Button, Modal, FormGroup, Input, Label } from "../components/Modal";
import { debounce } from "lodash";
import TemporaryAccessIndex from "./TemporaryAccessIndex";

let contextSubscriptionId = "";

// Types
interface SubscribedSku {
    id: string;
    skuId: string;
    skuPartNumber: string;
    appliesTo: string;
    capabilityStatus: string;
    consumedUnits: number;
    prepaidUnitsEnabled: number;
    prepaidUnitsSuspended: number;
    prepaidUnitsWarning: number;
    servicePlans: ServicePlan[];
}

interface ServicePlan {
    servicePlanId: string;
    servicePlanName: string;
    provisioningStatus: string;
    appliesTo: string;
}

interface LicenseDetail {
    id: string;
    skuId: string;
    skuPartNumber: string;
    servicePlans: ServicePlan[];
}

interface UserLicenseResult {
    success: boolean;
    errorMessage?: string;
    userIdOrUpn: string;
    licenseDetails: LicenseDetail[];
}

interface SubscribedSkusResult {
    success: boolean;
    errorMessage?: string;
    skus: SubscribedSku[];
}

interface SearchedUser {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    photo?: string;
}

interface AzureUserStatus {
    email: string;
    exists: boolean;
    entraIdUserId?: string;
    userPrincipalName?: string;
    mail?: string;
    displayName?: string;
    accountEnabled?: boolean;
    errorMessage?: string;
}

// Reusable License Management Component
interface RenderAzureLicenseManagementProps {
    organizationId: string;
    managedService?: AzureManagedService;
}

export const RenderAzureLicenseManagement = ({ organizationId, managedService }: RenderAzureLicenseManagementProps) => {
    const [subscribedSkus, setSubscribedSkus] = React.useState<SubscribedSku[]>([]);
    const [loadingSkus, setLoadingSkus] = React.useState(false);
    const [skusError, setSkusError] = React.useState<string | null>(null);

    const [searchQuery, setSearchQuery] = React.useState("");
    const [searchResults, setSearchResults] = React.useState<SearchedUser[]>([]);
    const [searchingUsers, setSearchingUsers] = React.useState(false);

    const [selectedUser, setSelectedUser] = React.useState<SearchedUser | null>(null);
    const [azureUserStatus, setAzureUserStatus] = React.useState<AzureUserStatus | null>(null);
    const [checkingAzureStatus, setCheckingAzureStatus] = React.useState(false);

    const [userLicenses, setUserLicenses] = React.useState<LicenseDetail[]>([]);
    const [loadingUserLicenses, setLoadingUserLicenses] = React.useState(false);

    const [showAssignModal, setShowAssignModal] = React.useState(false);
    const [showRemoveModal, setShowRemoveModal] = React.useState(false);
    const [selectedSkuForAction, setSelectedSkuForAction] = React.useState<SubscribedSku | null>(null);
    const [selectedLicenseForRemoval, setSelectedLicenseForRemoval] = React.useState<LicenseDetail | null>(null);
    const [actionLoading, setActionLoading] = React.useState(false);
    const [actionMessage, setActionMessage] = React.useState<{ type: "success" | "error"; text: string } | null>(null);

    // Track which licenses have expanded service plans (using skuId as key)
    const [expandedServicePlans, setExpandedServicePlans] = React.useState<Set<string>>(new Set());

    const toggleServicePlansExpanded = (skuId: string) => {
        setExpandedServicePlans((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(skuId)) {
                newSet.delete(skuId);
            } else {
                newSet.add(skuId);
            }
            return newSet;
        });
    };

    // Load subscribed SKUs
    const loadSubscribedSkus = async () => {
        setLoadingSkus(true);
        setSkusError(null);
        try {
            const response = await axios.get<SubscribedSkusResult>(`/Azure/Licenses/${organizationId}`);
            if (response.data.success) {
                setSubscribedSkus(response.data.skus || []);
            } else {
                setSkusError(response.data.errorMessage || "Failed to load licenses");
            }
        } catch (error: any) {
            setSkusError(error.response?.data?.error || error.message || "Failed to load licenses");
        } finally {
            setLoadingSkus(false);
        }
    };

    // Search users
    const searchUsers = async (query: string) => {
        if (!query || query.length < 2) {
            setSearchResults([]);
            return;
        }
        setSearchingUsers(true);
        try {
            const response = await axios.get("/api/users/searchUsers", {
                params: { query, skip: 0, limit: 10, includeStaff: true, includeStudents: true, includeParents: true, includeGuests: true },
            });
            setSearchResults(response.data || []);
        } catch (error) {
            console.error("Failed to search users", error);
            setSearchResults([]);
        } finally {
            setSearchingUsers(false);
        }
    };

    const debouncedSearch = React.useMemo(() => debounce(searchUsers, 300), []);

    React.useEffect(() => {
        debouncedSearch(searchQuery);
    }, [searchQuery]);

    React.useEffect(() => {
        if (organizationId) {
            loadSubscribedSkus();
        }
    }, [organizationId]);

    // Check Azure status for selected user
    const checkAzureStatus = async (user: SearchedUser) => {
        setSelectedUser(user);
        setAzureUserStatus(null);
        setUserLicenses([]);
        setCheckingAzureStatus(true);

        try {
            const response = await axios.get<AzureUserStatus>(`/Azure/CheckUserEmail/${organizationId}?email=${encodeURIComponent(user.email)}`);
            setAzureUserStatus(response.data);

            if (response.data.exists && response.data.userPrincipalName) {
                await loadUserLicenses(response.data.userPrincipalName);
            }
        } catch (error: any) {
            setAzureUserStatus({
                email: user.email,
                exists: false,
                errorMessage: error.response?.data?.error || error.message || "Failed to check Azure status",
            });
        } finally {
            setCheckingAzureStatus(false);
        }
    };

    // Load user licenses
    const loadUserLicenses = async (userIdOrUpn: string) => {
        setLoadingUserLicenses(true);
        try {
            const response = await axios.get<UserLicenseResult>(`/Azure/Licenses/${organizationId}/User/${encodeURIComponent(userIdOrUpn)}`);
            if (response.data.success) {
                setUserLicenses(response.data.licenseDetails || []);
            }
        } catch (error) {
            console.error("Failed to load user licenses", error);
        } finally {
            setLoadingUserLicenses(false);
        }
    };

    // Assign license
    const assignLicense = async () => {
        if (!azureUserStatus?.userPrincipalName || !selectedSkuForAction) return;

        setActionLoading(true);
        setActionMessage(null);
        try {
            await axios.post(`/Azure/Licenses/${organizationId}/User/${encodeURIComponent(azureUserStatus.userPrincipalName)}/Assign`, {
                skuIds: [selectedSkuForAction.skuId],
            });
            setActionMessage({ type: "success", text: `Successfully assigned ${selectedSkuForAction.skuPartNumber} license` });
            setShowAssignModal(false);
            // Refresh user licenses and SKUs
            await loadUserLicenses(azureUserStatus.userPrincipalName);
            await loadSubscribedSkus();
        } catch (error: any) {
            setActionMessage({ type: "error", text: error.response?.data?.error || error.message || "Failed to assign license" });
        } finally {
            setActionLoading(false);
        }
    };

    // Remove license
    const removeLicense = async () => {
        if (!azureUserStatus?.userPrincipalName || !selectedLicenseForRemoval) return;

        setActionLoading(true);
        setActionMessage(null);
        try {
            await axios.post(`/Azure/Licenses/${organizationId}/User/${encodeURIComponent(azureUserStatus.userPrincipalName)}/Remove`, {
                skuIds: [selectedLicenseForRemoval.skuId],
            });
            setActionMessage({ type: "success", text: `Successfully removed ${selectedLicenseForRemoval.skuPartNumber} license` });
            setShowRemoveModal(false);
            // Refresh user licenses and SKUs
            await loadUserLicenses(azureUserStatus.userPrincipalName);
            await loadSubscribedSkus();
        } catch (error: any) {
            setActionMessage({ type: "error", text: error.response?.data?.error || error.message || "Failed to remove license" });
        } finally {
            setActionLoading(false);
        }
    };

    const startAssignLicense = (sku: SubscribedSku) => {
        setSelectedSkuForAction(sku);
        setShowAssignModal(true);
        setActionMessage(null);
    };

    const startRemoveLicense = (license: LicenseDetail) => {
        setSelectedLicenseForRemoval(license);
        setShowRemoveModal(true);
        setActionMessage(null);
    };

    const clearSelectedUser = () => {
        setSelectedUser(null);
        setAzureUserStatus(null);
        setUserLicenses([]);
        setSearchQuery("");
        setSearchResults([]);
        setActionMessage(null);
    };

    // Check if user already has a specific license
    const userHasLicense = (skuId: string) => {
        return userLicenses.some((l) => l.skuId === skuId);
    };

    return (
        <div className="space-y-6">
            {/* Action Message */}
            {actionMessage && (
                <div className={`p-4 rounded-lg ${actionMessage.type === "success" ? "bg-green-50 border border-green-200" : "bg-red-50 border border-red-200"}`}>
                    <div className={`flex items-center ${actionMessage.type === "success" ? "text-green-800" : "text-red-800"}`}>
                        <FontAwesomeIcon icon={["fad", actionMessage.type === "success" ? "check-circle" : "exclamation-circle"]} className="w-5 h-5 mr-2" />
                        <span>{actionMessage.text}</span>
                    </div>
                </div>
            )}

            {/* User Search Section */}
            <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h4 className="text-lg font-medium text-gray-900 mb-4">
                    <FontAwesomeIcon icon={["fad", "search"]} className="mr-2 text-blue-600" />
                    Search User
                </h4>

                {!selectedUser ? (
                    <div className="space-y-3">
                        <div className="relative">
                            <Input type="text" placeholder="Search by name or email..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="w-full" />
                            {searchingUsers && (
                                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                    <FontAwesomeIcon icon={["fad", "spinner"]} className="animate-spin text-gray-400" />
                                </div>
                            )}
                        </div>

                        {searchResults.length > 0 && (
                            <div className="border border-gray-200 rounded-lg divide-y divide-gray-200 max-h-60 overflow-y-auto">
                                {searchResults.map((user) => (
                                    <button key={user.id} onClick={() => checkAzureStatus(user)} className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3">
                                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                                            {user.photo ? (
                                                <img src={user.photo} alt="" className="w-10 h-10 rounded-full object-cover" />
                                            ) : (
                                                <FontAwesomeIcon icon={["fad", "user"]} className="text-gray-500" />
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-medium text-gray-900 truncate">
                                                {user.firstName} {user.lastName}
                                            </div>
                                            <div className="text-sm text-gray-500 truncate">{user.email}</div>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="space-y-4">
                        <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <div className="flex items-center gap-3">
                                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-blue-200 flex items-center justify-center">
                                    {selectedUser.photo ? (
                                        <img src={selectedUser.photo} alt="" className="w-12 h-12 rounded-full object-cover" />
                                    ) : (
                                        <FontAwesomeIcon icon={["fad", "user"]} className="text-blue-600 text-xl" />
                                    )}
                                </div>
                                <div>
                                    <div className="font-medium text-gray-900">
                                        {selectedUser.firstName} {selectedUser.lastName}
                                    </div>
                                    <div className="text-sm text-gray-600">{selectedUser.email}</div>
                                </div>
                            </div>
                            <Button variant="secondary" size="sm" onClick={clearSelectedUser}>
                                <FontAwesomeIcon icon={["fad", "times"]} className="mr-1" />
                                Clear
                            </Button>
                        </div>

                        {/* Azure Status */}
                        {checkingAzureStatus && (
                            <div className="flex items-center text-gray-600">
                                <FontAwesomeIcon icon={["fad", "spinner"]} className="animate-spin mr-2" />
                                Checking Azure Entra ID status...
                            </div>
                        )}

                        {azureUserStatus && !checkingAzureStatus && (
                            <div className={`rounded-lg p-4 ${azureUserStatus.exists ? "bg-green-50 border border-green-200" : "bg-red-50 border border-red-200"}`}>
                                {azureUserStatus.exists ? (
                                    <div className="space-y-2">
                                        <div className="flex items-center text-green-800">
                                            <FontAwesomeIcon icon={["fad", "check-circle"]} className="w-5 h-5 mr-2" />
                                            <span className="font-medium">User found in Entra ID</span>
                                        </div>
                                        <div className="ml-7 space-y-1 text-sm text-gray-700">
                                            {azureUserStatus.displayName && (
                                                <div>
                                                    <span className="font-medium">Display Name:</span> {azureUserStatus.displayName}
                                                </div>
                                            )}
                                            {azureUserStatus.userPrincipalName && (
                                                <div>
                                                    <span className="font-medium">UPN:</span> {azureUserStatus.userPrincipalName}
                                                </div>
                                            )}
                                            <div>
                                                <span className="font-medium">Account Status:</span>
                                                <span className={`ml-1 ${azureUserStatus.accountEnabled ? "text-green-700" : "text-red-700"}`}>
                                                    {azureUserStatus.accountEnabled ? "Enabled" : "Disabled"}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="space-y-2">
                                        <div className="flex items-center text-red-800">
                                            <FontAwesomeIcon icon={["fad", "exclamation-circle"]} className="w-5 h-5 mr-2" />
                                            <span className="font-medium">User not found in Entra ID</span>
                                        </div>
                                        {azureUserStatus.errorMessage && <div className="ml-7 text-sm text-red-700">{azureUserStatus.errorMessage}</div>}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* User's Current Licenses */}
                        {azureUserStatus?.exists && (
                            <div>
                                <h5 className="text-sm font-medium text-gray-700 mb-2">Current Licenses</h5>
                                {loadingUserLicenses ? (
                                    <div className="flex items-center text-gray-600 text-sm">
                                        <FontAwesomeIcon icon={["fad", "spinner"]} className="animate-spin mr-2" />
                                        Loading licenses...
                                    </div>
                                ) : userLicenses.length > 0 ? (
                                    <div className="space-y-2">
                                        {userLicenses.map((license, index) => (
                                            <div key={index} className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                                                <div className="flex items-center justify-between">
                                                    <div>
                                                        <div className="font-medium text-gray-900">{license.skuPartNumber}</div>
                                                        <div className="text-xs text-gray-500">SKU ID: {license.skuId}</div>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <FontAwesomeIcon icon={["fad", "id-card"]} className="w-5 h-5 text-purple-600" />
                                                        <Button variant="danger" size="sm" onClick={() => startRemoveLicense(license)}>
                                                            <FontAwesomeIcon icon={["fad", "trash"]} className="mr-1" />
                                                            Remove
                                                        </Button>
                                                    </div>
                                                </div>
                                                {license.servicePlans?.length > 0 && (
                                                    <div className="mt-2 pt-2 border-t border-purple-200">
                                                        <div className="text-xs font-medium text-gray-600 mb-1">Service Plans:</div>
                                                        <div className="flex flex-wrap gap-1">
                                                            {(expandedServicePlans.has(license.skuId) ? license.servicePlans : license.servicePlans.slice(0, 5)).map((plan, planIndex) => (
                                                                <span
                                                                    key={planIndex}
                                                                    className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                                                        plan.provisioningStatus === "Success"
                                                                            ? "bg-green-100 text-green-800"
                                                                            : plan.provisioningStatus === "Disabled"
                                                                              ? "bg-gray-100 text-gray-600"
                                                                              : "bg-yellow-100 text-yellow-800"
                                                                    }`}
                                                                    title={`${plan.servicePlanName} - ${plan.provisioningStatus}`}
                                                                >
                                                                    {plan.servicePlanName}
                                                                </span>
                                                            ))}
                                                            {license.servicePlans.length > 5 && (
                                                                <button
                                                                    onClick={() => toggleServicePlansExpanded(license.skuId)}
                                                                    className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700 hover:bg-blue-200 cursor-pointer transition-colors"
                                                                >
                                                                    {expandedServicePlans.has(license.skuId) ? (
                                                                        <>
                                                                            <FontAwesomeIcon icon={["fad", "chevron-up"]} className="mr-1 w-2 h-2" />
                                                                            Show less
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <FontAwesomeIcon icon={["fad", "chevron-down"]} className="mr-1 w-2 h-2" />+{license.servicePlans.length - 5} more
                                                                        </>
                                                                    )}
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-sm text-gray-500 bg-gray-50 rounded-lg p-3">No licenses assigned to this user.</div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Available Licenses (SKUs) Section */}
            <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                    <h4 className="text-lg font-medium text-gray-900">
                        <FontAwesomeIcon icon={["fad", "id-card"]} className="mr-2 text-purple-600" />
                        Available Licenses
                    </h4>
                    <Button variant="secondary" size="sm" onClick={loadSubscribedSkus} disabled={loadingSkus}>
                        <FontAwesomeIcon icon={["fad", loadingSkus ? "spinner" : "refresh"]} className={`mr-1 ${loadingSkus ? "animate-spin" : ""}`} />
                        Refresh
                    </Button>
                </div>

                {loadingSkus && (
                    <div className="flex items-center justify-center py-8 text-gray-500">
                        <FontAwesomeIcon icon={["fad", "spinner"]} className="animate-spin mr-2" />
                        Loading available licenses...
                    </div>
                )}

                {skusError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <div className="flex items-center text-red-800">
                            <FontAwesomeIcon icon={["fad", "exclamation-circle"]} className="w-5 h-5 mr-2" />
                            <span>{skusError}</span>
                        </div>
                    </div>
                )}

                {!loadingSkus && !skusError && subscribedSkus.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                        <FontAwesomeIcon icon={["fad", "box-open"]} className="w-12 h-12 mb-3 text-gray-300" />
                        <p>No licenses found for this organization.</p>
                    </div>
                )}

                {!loadingSkus && subscribedSkus.length > 0 && (
                    <div className="space-y-3">
                        {subscribedSkus.map((sku, index) => {
                            const availableUnits = sku.prepaidUnitsEnabled - sku.consumedUnits;
                            const hasLicense = selectedUser && azureUserStatus?.exists ? userHasLicense(sku.skuId) : false;

                            return (
                                <div key={index} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                                    <div className="flex items-center justify-between">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2">
                                                <span className="font-medium text-gray-900">{sku.skuPartNumber}</span>
                                                {sku.capabilityStatus === "Enabled" ? (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">Active</span>
                                                ) : (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">{sku.capabilityStatus}</span>
                                                )}
                                                {hasLicense && (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                                                        <FontAwesomeIcon icon={["fad", "check"]} className="mr-1" />
                                                        Assigned
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-xs text-gray-500 mt-1">SKU ID: {sku.skuId}</div>
                                            <div className="flex items-center gap-4 mt-2 text-sm">
                                                <span className="text-gray-600">
                                                    <span className="font-medium">{sku.consumedUnits}</span> / {sku.prepaidUnitsEnabled} used
                                                </span>
                                                <span className={`${availableUnits > 0 ? "text-green-600" : "text-red-600"}`}>{availableUnits} available</span>
                                            </div>
                                            {/* Progress bar */}
                                            <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                                                <div
                                                    className={`h-2 rounded-full ${availableUnits > 0 ? "bg-blue-600" : "bg-red-500"}`}
                                                    style={{
                                                        width: `${Math.min((sku.consumedUnits / sku.prepaidUnitsEnabled) * 100, 100)}%`,
                                                    }}
                                                ></div>
                                            </div>
                                        </div>
                                        <div className="ml-4">
                                            {selectedUser && azureUserStatus?.exists && !hasLicense && availableUnits > 0 && (
                                                <Button variant="primary" size="sm" onClick={() => startAssignLicense(sku)}>
                                                    <FontAwesomeIcon icon={["fad", "plus"]} className="mr-1" />
                                                    Assign
                                                </Button>
                                            )}
                                            {hasLicense && (
                                                <span className="text-sm text-purple-600 font-medium">
                                                    <FontAwesomeIcon icon={["fad", "check-circle"]} className="mr-1" />
                                                    Assigned
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Assign License Modal */}
            <Modal isOpen={showAssignModal} onClose={() => setShowAssignModal(false)} title="Assign License" icon={["fad", "id-card"]} iconColor="text-blue-600" size="md">
                <div className="p-6">
                    <p className="text-gray-700 mb-4">
                        Are you sure you want to assign <strong>{selectedSkuForAction?.skuPartNumber}</strong> license to{" "}
                        <strong>
                            {selectedUser?.firstName} {selectedUser?.lastName}
                        </strong>
                        ?
                    </p>
                    {selectedSkuForAction && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                            <div className="text-blue-800">
                                <span className="font-medium">Available:</span> {selectedSkuForAction.prepaidUnitsEnabled - selectedSkuForAction.consumedUnits} licenses
                            </div>
                        </div>
                    )}
                </div>
                <div className="border-t border-gray-200 bg-gray-50 px-6 py-4 flex justify-end space-x-3">
                    <Button variant="secondary" onClick={() => setShowAssignModal(false)} disabled={actionLoading}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={assignLicense} disabled={actionLoading}>
                        {actionLoading ? (
                            <>
                                <FontAwesomeIcon icon={["fad", "spinner"]} className="animate-spin mr-2" />
                                Assigning...
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={["fad", "check"]} className="mr-2" />
                                Assign License
                            </>
                        )}
                    </Button>
                </div>
            </Modal>

            {/* Remove License Modal */}
            <Modal isOpen={showRemoveModal} onClose={() => setShowRemoveModal(false)} title="Remove License" icon={["fad", "trash"]} iconColor="text-red-600" size="md">
                <div className="p-6">
                    <p className="text-gray-700 mb-4">
                        Are you sure you want to remove <strong>{selectedLicenseForRemoval?.skuPartNumber}</strong> license from{" "}
                        <strong>
                            {selectedUser?.firstName} {selectedUser?.lastName}
                        </strong>
                        ?
                    </p>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                        <div className="flex items-start gap-2">
                            <FontAwesomeIcon icon={["fad", "exclamation-triangle"]} className="text-yellow-600 mt-0.5" />
                            <div className="text-sm text-yellow-800">
                                <strong>Warning:</strong> Removing this license will revoke the user's access to associated services and applications.
                            </div>
                        </div>
                    </div>
                </div>
                <div className="border-t border-gray-200 bg-gray-50 px-6 py-4 flex justify-end space-x-3">
                    <Button variant="secondary" onClick={() => setShowRemoveModal(false)} disabled={actionLoading}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={removeLicense} disabled={actionLoading}>
                        {actionLoading ? (
                            <>
                                <FontAwesomeIcon icon={["fad", "spinner"]} className="animate-spin mr-2" />
                                Removing...
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={["fad", "trash"]} className="mr-2" />
                                Remove License
                            </>
                        )}
                    </Button>
                </div>
            </Modal>
        </div>
    );
};

// Main Azure Index Page
type AzureTab = "licenses" | "temporary-access";

const AzureIndex = ({ organizationId, managedService }: { organizationId: string; managedService: AzureManagedService }) => {
    const [isAdmin, setIsAdmin] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [activeTab, setActiveTab] = React.useState<AzureTab>("licenses");

    React.useEffect(() => {
        // Check if user is admin
        GetCurrentLoggedInUser().then((user) => {
            const adminClaim = user.claims.find((c) => c.type === ROLE_CLAIM && (c.value === "Admin" || c.value === "SuperAdmin"));
            setIsAdmin(!!adminClaim);
        });

        return () => {};
    }, []);

    React.useEffect(() => {
        if (organizationId) {
        }
    }, [organizationId]);

    return (
        <div className="p-6">
            {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                    <div className="flex items-center text-red-800">
                        <FontAwesomeIcon icon={["fad", "exclamation-circle"]} className="w-5 h-5 mr-2" />
                        <span>{error}</span>
                    </div>
                </div>
            )}

            {!managedService && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
                    <FontAwesomeIcon icon={["fad", "exclamation-triangle"]} className="w-12 h-12 text-yellow-500 mb-4" />
                    <h3 className="text-lg font-medium text-yellow-800 mb-2">Azure Integration Not Configured</h3>
                    <p className="text-yellow-700">Please configure an Azure managed service for this organization to manage licenses.</p>
                </div>
            )}

            {managedService && organizationId && (
                <div className="space-y-6">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center">
                                <FontAwesomeIcon icon={["fad", "search"]} className="text-white text-xl" />
                            </div>
                            <div>
                                <h3 className="font-medium text-gray-900">Azure Entra ID</h3>
                                <p className="text-sm text-gray-600">Tenant ID: {managedService.configuration?.tenantID}</p>
                            </div>
                            {managedService.configuration?.adminConsentGranted && (
                                <span className="ml-auto inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                                    <FontAwesomeIcon icon={["fad", "check-circle"]} className="mr-1" />
                                    Connected
                                </span>
                            )}
                        </div>
                    </div>

                    <div className="border-b border-gray-200">
                        <nav className="-mb-px flex space-x-8">
                            <button
                                onClick={() => setActiveTab("licenses")}
                                className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                                    activeTab === "licenses" ? "border-blue-500 text-blue-600" : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                                }`}
                            >
                                <FontAwesomeIcon icon={["fad", "id-card"]} className="mr-2" />
                                License Management
                            </button>
                            <button
                                onClick={() => setActiveTab("temporary-access")}
                                className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                                    activeTab === "temporary-access" ? "border-blue-500 text-blue-600" : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                                }`}
                            >
                                <FontAwesomeIcon icon={["fad", "users-cog"]} className="mr-2" />
                                Temporary Access
                            </button>
                        </nav>
                    </div>

                    {activeTab === "licenses" && <RenderAzureLicenseManagement organizationId={organizationId} managedService={managedService} />}
                    {activeTab === "temporary-access" && <TemporaryAccessIndex organizationId={organizationId} />}
                </div>
            )}
        </div>
    );
};

export default AzureIndex;
